import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";

const VoiceVisualizer = () => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState(null);

  const drawIdle = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const c = canvas.getContext("2d");
    const { width: w, height: h } = canvas;
    c.clearRect(0, 0, w, h);
    const cx = w / 2;
    const cy = h / 2;
    const base = Math.min(w, h) * 0.28;
    c.beginPath();
    for (let i = 0; i <= 360; i++) {
      const a = (i * Math.PI) / 180;
      const x = cx + Math.cos(a) * base;
      const y = cy + Math.sin(a) * base;
      i === 0 ? c.moveTo(x, y) : c.lineTo(x, y);
    }
    c.strokeStyle = "rgba(212,175,55,0.35)";
    c.lineWidth = 1;
    c.stroke();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const size = Math.min(canvas.parentElement.clientWidth, 420);
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      canvas.style.width = `${size}px`;
      canvas.style.height = `${size}px`;
      if (!active) drawIdle();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [active]);

  useEffect(() => () => stop(), []);

  const draw = () => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const c = canvas.getContext("2d");
    const { width: w, height: h } = canvas;
    const cx = w / 2;
    const cy = h / 2;
    const base = Math.min(w, h) * 0.28;
    const data = new Uint8Array(analyser.fftSize);

    const loop = () => {
      analyser.getByteTimeDomainData(data);
      c.clearRect(0, 0, w, h);

      c.beginPath();
      c.arc(cx, cy, base * 0.72, 0, Math.PI * 2);
      c.strokeStyle = "rgba(209,213,219,0.14)";
      c.lineWidth = 1;
      c.stroke();

      const points = 240;
      const step = Math.floor(data.length / points);
      c.beginPath();
      for (let i = 0; i <= points; i++) {
        const v = (data[(i * step) % data.length] - 128) / 128;
        const a = (i / points) * Math.PI * 2 - Math.PI / 2;
        const r = base + v * base * 0.55;
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        i === 0 ? c.moveTo(x, y) : c.lineTo(x, y);
      }
      c.closePath();
      c.shadowColor = "rgba(212,175,55,0.8)";
      c.shadowBlur = 18;
      c.strokeStyle = "rgba(212,175,55,0.95)";
      c.lineWidth = 2;
      c.stroke();

      c.shadowBlur = 0;
      c.beginPath();
      for (let i = 0; i <= points; i++) {
        const v = (data[(i * step) % data.length] - 128) / 128;
        const a = (i / points) * Math.PI * 2 - Math.PI / 2;
        const r = base * 0.82 + v * base * 0.3;
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a) * r;
        i === 0 ? c.moveTo(x, y) : c.lineTo(x, y);
      }
      c.closePath();
      c.strokeStyle = "rgba(243,229,171,0.35)";
      c.lineWidth = 1;
      c.stroke();

      rafRef.current = requestAnimationFrame(loop);
    };
    loop();
  };

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      analyserRef.current = analyser;
      setActive(true);
      draw();
    } catch (e) {
      setError("Microphone access was declined. Allow mic access to see your voice.");
    }
  };

  const stop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (ctxRef.current) ctxRef.current.close();
    streamRef.current = null;
    ctxRef.current = null;
    analyserRef.current = null;
    setActive(false);
    setTimeout(drawIdle, 50);
  };

  return (
    <div data-testid="voice-visualizer" className="flex flex-col items-center">
      <div className="relative">
        <div className="glow-pulse pointer-events-none absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.1)_0%,transparent_65%)]" />
        <canvas ref={canvasRef} data-testid="voice-visualizer-canvas" className="relative" />
      </div>
      <button
        data-testid="voice-visualizer-toggle"
        onClick={active ? stop : start}
        className={active ? "btn-outline mt-10" : "btn-solid mt-10"}
      >
        {active ? <Square size={12} strokeWidth={1.5} /> : <Mic size={13} strokeWidth={1.5} />}
        {active ? "Release the Mic" : "Speak — See Your Voice"}
      </button>
      {error && (
        <p data-testid="voice-visualizer-error" className="mt-4 text-xs font-light text-[#f3e5ab]/70">
          {error}
        </p>
      )}
      <p className="mt-6 max-w-sm text-center text-xs font-extralight leading-relaxed text-white/40">
        A glimpse of Tundala — your voice, rendered as living geometry. Nothing is recorded or stored.
      </p>
    </div>
  );
};

export default VoiceVisualizer;
